import React from 'react';
import Helmet from 'react-helmet';
import {
  type HreflangLink,
  LOCALE_REPLACEMENT_FOR_HREF_LANG_CODE,
} from '@repo/gatsby-localization-helpers';

const getHrefLang = locale =>
  LOCALE_REPLACEMENT_FOR_HREF_LANG_CODE?.[locale] || locale;

type HelmetMeta = JSX.IntrinsicElements['meta'];
type HelmetLink = JSX.IntrinsicElements['link'];
type HelmetScript = JSX.IntrinsicElements['script'];

interface HeadProps {
  description?: string;
  lang?: string;
  meta?: HelmetMeta[];
  title?: string;
  keywords?: string;
  linkTags?: HelmetLink[];
  children?: React.ReactNode;
  scriptTags?: HelmetScript[];
  noIndex?: boolean;
  noFollow?: boolean;
  hreflangs?: HreflangLink[];
}

function Head({
  description = '',
  lang = 'en',
  meta = [],
  title = '',
  keywords = '',
  linkTags = [],
  children = null,
  scriptTags = [],
  noIndex = false,
  noFollow = false,
  hreflangs = [],
}: Readonly<HeadProps>) {
  let noIndexFollow = [];
  if (process.env.GATSBY_ROBOTS_INDEX === 'true') {
    if (noIndex) noIndexFollow.push('noindex');
    else noIndexFollow.push('index');

    if (noFollow) noIndexFollow.push('nofollow');
    else noIndexFollow.push('follow');
  } else {
    noIndexFollow = ['noindex', 'nofollow'];
  }

  const hreflangLinks = hreflangs.map(data => (
    <link
      rel="alternate"
      hrefLang={getHrefLang(data.lang)}
      href={data.url}
      key={data.url}
    />
  ));

  return (
    <Helmet
      htmlAttributes={{ lang: getHrefLang(lang) }}
      title={title}
      meta={[
        {
          name: 'robots',
          content: noIndexFollow?.length && noIndexFollow.join(','),
        },
        {
          name: 'description',
          content: description,
        },
        {
          name: 'keywords',
          content: keywords,
        },
        ...meta,
      ]}
    >
      {linkTags.map(linkTag => linkTag)}
      {hreflangLinks.map(link => link)}
      {children}
      {scriptTags.map(scriptTag => scriptTag)}
    </Helmet>
  );
}

export default Head;
